import React, { useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import { getScoValue } from "../../../services/utils.ts";
import { ExtractionObject } from "../../../services/stixify_api.ts";
import { useNavigate } from "react-router-dom";

const REACT_APP_VULMATCH_WEB_URL = process.env.REACT_APP_VULMATCH_WEB_URL;
const REACT_APP_CTIBUTLER_WEB_URL = process.env.REACT_APP_CTIBUTLER_WEB_URL;
const CTIBUTLER_ATTACK_TYPE_CONVERSION_DICT = {
  "enterprise-attack": "attack-enterprise",
  "mobile-attack": "attack-mobile",
  "ics-attack": "attack-ics",
};
const TTP_TYPES = new Set([
  "attack-pattern",
  "campaign",
  "course-of-action",
  "identity",
  "infrastructure",
  "intrusion-set",
  "location",
  "malware",
  "note",
  "report",
  "threat-actor",
  "tool",
]);

const SCO_TYPES = new Set([
  "domain-name",
  "autonomous-system",
  "bank-account",
  "bank-card",
  "cryptocurrency-transaction",
  "cryptocurrency-wallet",
  "directory",
  "email-addr",
  "file",
  "ipv4-addr",
  "ipv6-addr",
  "mac-addr",
  "network-traffic",
  "phone-number",
  "url",
  "user-agent",
  "windows-registry-key",
]);

type ExtractionTablesProps = {
  objects: ExtractionObject[];
  getObservationSearchUrl: (object: ExtractionObject) => string;
  removeObject: (object: ExtractionObject) => void;
  showDelete: boolean;
};

export const ExtractionTables: React.FC<ExtractionTablesProps> = ({
  objects,
  getObservationSearchUrl,
  removeObject,
  showDelete,
}: ExtractionTablesProps) => {
  const navigate = useNavigate();

  const getObservables = () => {
    return objects.filter((object) => SCO_TYPES.has(object.type));
  };

  const isSDOType = (object: ExtractionObject) => {
    return (
      TTP_TYPES.has(object.type) &&
      !isMitreAttack(object) &&
      !object.x_capec_version
    );
  };

  const getTTps = () => {
    return objects.filter((object) => isSDOType(object));
  };

  const getCVEs = () => {
    return objects.filter((object) => object.type === "vulnerability");
  };

  const getCWEs = () => {
    return objects.filter((object) => object.type === "weakness");
  };

  const getCapecs = () => {
    return objects.filter((object) => object.x_capec_version);
  };

  const isMitreAttack = (object: ExtractionObject) => {
    const keywords = ["enterprise-attack", "ics-attack", "mobile-attack"];
    if (object.x_mitre_domains) {
      return (
        object.x_mitre_domains.findIndex((item) => keywords.includes(item)) > -1
      );
    }
    return false;
  };
  const getObjectReferenceID = (object: ExtractionObject) => {
    if (!object.external_references) return;
    return object.external_references[0].external_id;
  };
  const getMitreAttacks = () => {
    return objects.filter((object) => isMitreAttack(object));
  };

  const getCtiButlerURL = (object: ExtractionObject) => {
    const keywords = ["enterprise-attack", "ics-attack", "mobile-attack"];
    let objectType: string | undefined = "";
    if (object.x_mitre_domains) {
      objectType = object.x_mitre_domains.find((item) =>
        keywords.includes(item)
      );
      if (!objectType) return "#";
      const knowledgebaseUrlPath =
        CTIBUTLER_ATTACK_TYPE_CONVERSION_DICT[objectType];
      return `${REACT_APP_CTIBUTLER_WEB_URL}/knowledgebases/${knowledgebaseUrlPath}/${object.id}`;
    }
  };

  return (
    <>
      <TableContainer sx={{ marginTop: "3rem" }}>
        <Typography variant="h5">Observables</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell sx={{ width: "38%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getObservables().length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>No extractions found</TableCell>
              </TableRow>
            )}
            {getObservables().map((object, index) => (
              <TableRow className="ioc-row" key={index}>
                <TableCell sx={{ width: "20%" }}>{object.type}</TableCell>
                <TableCell>{getScoValue(object)}</TableCell>
                <TableCell sx={{ width: "38%" }}>
                  <Button
                    color="primary"
                    variant="contained"
                    sx={{ textTransform: "uppercase" }}
                    onClick={() => {
                      navigate(getObservationSearchUrl(object));
                    }}
                  >
                    View all posts
                  </Button>
                  {showDelete && (
                    <Button
                      color="error"
                      variant="contained"
                      sx={{
                        textTransform: "uppercase",
                        marginLeft: "2rem",
                      }}
                      onClick={() => {
                        removeObject(object);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer sx={{ marginTop: "3rem" }}>
        <Typography variant="h5">TTPs</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>Type</TableCell>
              <TableCell>Value</TableCell>
              <TableCell sx={{ width: "38%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getTTps().length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>No extractions found</TableCell>
              </TableRow>
            )}
            {getTTps().map((object, index) => (
              <TableRow key={index}>
                <TableCell sx={{ width: "20%" }}>{object.type}</TableCell>
                <TableCell>{object.name ?? object.value}</TableCell>
                <TableCell sx={{ width: "38%" }}>
                  <Button
                    color="primary"
                    variant="contained"
                    sx={{ textTransform: "uppercase" }}
                    onClick={() => {
                      navigate(getObservationSearchUrl(object));
                    }}
                  >
                    View all posts
                  </Button>
                  {object.type === "vulnerability" && (
                    <a
                      href={`${REACT_APP_VULMATCH_WEB_URL}/vulnerabilities/${object.name}`}
                    >
                      <Button
                        color="primary"
                        variant="contained"
                        sx={{
                          textTransform: "uppercase",
                          margin: "0.5rem",
                        }}
                      >
                        View in vulmatch
                      </Button>
                    </a>
                  )}
                  {object.type === "weakness" && (
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={`${REACT_APP_CTIBUTLER_WEB_URL}/knowledgebases/cwe/${object.id}`}
                    >
                      <Button
                        sx={{
                          margin: "0.5rem",
                        }}
                        variant="contained"
                      >
                        View in CTI Butler
                      </Button>
                    </a>
                  )}
                  {showDelete && (
                    <Button
                      color="error"
                      variant="contained"
                      sx={{
                        textTransform: "uppercase",
                        marginLeft: "2rem",
                      }}
                      onClick={() => {
                        removeObject(object);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer sx={{ marginTop: "3rem" }}>
        <Typography variant="h5">CVEs</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>CVE ID</TableCell>
              <TableCell>Cve Description</TableCell>
              <TableCell sx={{ width: "38%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getCVEs().length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>No extractions found</TableCell>
              </TableRow>
            )}
            {getCVEs().map((object, index) => (
              <TableRow key={index}>
                <TableCell sx={{ width: "20%" }}>{object.name}</TableCell>
                <TableCell>{object.description}</TableCell>
                <TableCell sx={{ width: "38%" }}>
                  <Button
                    color="primary"
                    variant="contained"
                    sx={{ textTransform: "uppercase" }}
                    onClick={() => {
                      navigate(getObservationSearchUrl(object));
                    }}
                  >
                    View all posts
                  </Button>
                  {object.type === "vulnerability" && (
                    <a
                      href={`${REACT_APP_VULMATCH_WEB_URL}/vulnerabilities/${object.name}`}
                    >
                      <Button
                        color="primary"
                        variant="contained"
                        sx={{
                          textTransform: "uppercase",
                          margin: "0.5rem",
                        }}
                      >
                        View in vulmatch
                      </Button>
                    </a>
                  )}
                  {showDelete && (
                    <Button
                      color="error"
                      variant="contained"
                      sx={{
                        textTransform: "uppercase",
                        marginLeft: "2rem",
                      }}
                      onClick={() => {
                        removeObject(object);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer sx={{ marginTop: "3rem" }}>
        <Typography variant="h5">ATT&CK</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>ATT&CK ID</TableCell>
              <TableCell>ATT&CK Name</TableCell>
              <TableCell sx={{ width: "38%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getMitreAttacks().length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>No extractions found</TableCell>
              </TableRow>
            )}
            {objects
              .filter((object) => isMitreAttack(object))
              .map((object, index) => (
                <TableRow className="ioc-row" key={index}>
                  <TableCell sx={{ width: "20%" }}>
                    {getObjectReferenceID(object)}
                  </TableCell>
                  <TableCell>{object.name}</TableCell>
                  <TableCell sx={{ width: "38%" }}>
                    <Button
                      color="primary"
                      variant="contained"
                      sx={{ textTransform: "uppercase" }}
                      onClick={() => {
                        navigate(getObservationSearchUrl(object));
                      }}
                    >
                      View all posts
                    </Button>
                    <a
                      target="_blank"
                      rel="noreferrer"
                      href={getCtiButlerURL(object)}
                    >
                      <Button
                        sx={{
                          margin: "0.5rem",
                        }}
                        variant="contained"
                      >
                        View in CTI Butler
                      </Button>
                    </a>
                    {showDelete && (
                      <Button
                        color="error"
                        variant="contained"
                        sx={{
                          textTransform: "uppercase",
                          marginLeft: "2rem",
                        }}
                        onClick={() => {
                          removeObject(object);
                        }}
                      >
                        Delete
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer sx={{ marginTop: "3rem" }}>
        <Typography variant="h5">CWEs</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>CWE ID</TableCell>
              <TableCell>CWE Name</TableCell>
              <TableCell sx={{ width: "38%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getCWEs().length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>No extractions found</TableCell>
              </TableRow>
            )}
            {getCWEs().map((object, index) => (
              <TableRow key={index}>
                <TableCell sx={{ width: "20%" }}>
                  {getObjectReferenceID(object)}
                </TableCell>
                <TableCell>{object.name}</TableCell>
                <TableCell sx={{ width: "38%" }}>
                  <Button
                    color="primary"
                    variant="contained"
                    sx={{ textTransform: "uppercase" }}
                    onClick={() => {
                      navigate(getObservationSearchUrl(object));
                    }}
                  >
                    View all posts
                  </Button>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`${REACT_APP_CTIBUTLER_WEB_URL}/knowledgebases/cwe/${object.id}`}
                  >
                    <Button
                      sx={{
                        margin: "0.5rem",
                      }}
                      variant="contained"
                    >
                      View in CTI Butler
                    </Button>
                  </a>
                  {showDelete && (
                    <Button
                      color="error"
                      variant="contained"
                      sx={{
                        textTransform: "uppercase",
                        marginLeft: "2rem",
                      }}
                      onClick={() => {
                        removeObject(object);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TableContainer sx={{ marginTop: "3rem" }}>
        <Typography variant="h5">CAPEC</Typography>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>CAPEC ID</TableCell>
              <TableCell>CAPEC Name</TableCell>
              <TableCell sx={{ width: "38%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {getCapecs().length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>No extractions found</TableCell>
              </TableRow>
            )}
            {getCapecs().map((object, index) => (
              <TableRow key={index}>
                <TableCell sx={{ width: "20%" }}>
                  {getObjectReferenceID(object)}
                </TableCell>
                <TableCell>{object.name}</TableCell>
                <TableCell sx={{ width: "38%" }}>
                  <Button
                    color="primary"
                    variant="contained"
                    sx={{ textTransform: "uppercase" }}
                    onClick={() => {
                      navigate(getObservationSearchUrl(object));
                    }}
                  >
                    View all posts
                  </Button>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={`${REACT_APP_CTIBUTLER_WEB_URL}/knowledgebases/capec/${object.id}`}
                  >
                    <Button
                      sx={{
                        margin: "0.5rem",
                      }}
                      variant="contained"
                    >
                      View in CTI Butler
                    </Button>
                  </a>
                  {showDelete && (
                    <Button
                      color="error"
                      variant="contained"
                      sx={{
                        textTransform: "uppercase",
                        marginLeft: "2rem",
                      }}
                      onClick={() => {
                        removeObject(object);
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
